import { CSSProperties, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import PlaylistingCampaignDetails from "pages/MarketingPage/Playlisting/CampaignDetails";
import VideoCampaignDetails from "pages/MarketingPage/VideoAds/CampaignDetails";
import PreSaveDetails from "../MarketingPage/PreSave/Details/PreSaveDetails";
import Loader from "elements/Loader";
import PageSymphony from "elements/PageSymphony";
import useScreen from "Hooks/useScreen";
import { styx } from "modules/Utils";
import CampaignDetailsV2 from "pages/CampaignDetailsV2";
import useCampaignDetails from "Hooks/useCampaignDetails";
import { AdPlatform, PLAYBOOK_TYPE_INFO, PlaybookType } from "modules/Const";
import TagSymphony from "elements/TagSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import FbTokenExpired from "pages/MarketingPage/Components/FbTokenExpired";
import { removeCampaignError_api } from "api/campaignApi";

/** Label shown when the campaign data is loading */
const LOADING_LABEL = "Loading Campaign Data...";

// NOTE: component()
const MarketingCampaignDetails = () => {
    const history = useHistory()
    const params = useParams<{ campaignId?: string }>();
    const { cssCalc } = useScreen();

    /** Campaign ID from URL parameters (converted from string to number) */
    const { campaignId: campaignIdString } = params; // NOTE: it's a string because it's coming from the URL
    const campaignId = Number(campaignIdString)

    const {
        isLoadingCampaign,
        getCampaign,
        campaign,
    } = useCampaignDetails()

    useEffect(() => {
        getCampaign(campaignId)
    }, [campaignId]);

    /**
     * Determines which campaign details component to render based on campaign type and playbook
     * @returns The appropriate campaign details component
     */
    const renderCampaignDetails = () => {
        const campaignType = campaign?.campaign_metadata?.campaign_type;
        const playbook: PlaybookType = (campaign?.campaign_metadata?.playbook || campaignType) as PlaybookType;

        const adPlatform = PLAYBOOK_TYPE_INFO[playbook]?.adPlatform;

        if (campaign?.campaign_metadata.error?.id === "FB_ACCESS_TOKEN_INVALID") {
            return (
                <FbTokenExpired
                    onSuccess={async () => {
                        await removeCampaignError_api(campaign)
                        await getCampaign(campaignId)
                    }}
                    campaignId={campaign.id}
                    campaignType={campaign.campaign_metadata.playbook}
                />
            )
        }

        switch (adPlatform) {
            case AdPlatform.META:
            case AdPlatform.TIKTOK:
                return (
                    <CampaignDetailsV2 className="campaignDetailsV2_InstagramOrTikTok" />
                )
            case AdPlatform.YOUTUBE:
                return (
                    <VideoCampaignDetails
                        campaignDetails={campaign}
                        handleGoBack={() => history.push('/marketing')}
                    />
                )
            case AdPlatform.PLAYLISTS:
                return (
                    <PlaylistingCampaignDetails />
                )
            default:
                if (campaignType === 'pre_save') {
                    return <PreSaveDetails className="preSaveDetailsFromMarketingCampaignDetails" />
                } else {
                    return (
                        <div 
                            className="unsupportedCampaignDetails" 
                            style={sty.unsupportedCampaignDetails}
                        >
                            <div>
                                Unsupported campaign / playbook type
                            </div>
                            <TagSymphony className="unsupportedCampaignTag" title={playbook} />
                            <ButtonSymphony
                                className="backToMarketingButton"
                                onClick={() => history.push('/marketing')}
                            >
                                Go Back to Marketing
                            </ButtonSymphony>
                        </div>
                    )
                }
        }
    };

    // NOTE: render()
    return (
        <PageSymphony
            className="marketingCampaignDetailsComponent"
            isContent
        >
            {isLoadingCampaign ?
                <div
                    className="marketingCampaignDetailsLoading"
                    style={styx(sty.marketingCampaignDetailsLoading, { height: cssCalc() })}
                >
                    <Loader className="marketingCampaignDetailsLoading" size={44} />
                    <div className="mt-1 text-center">
                        {LOADING_LABEL}
                    </div>
                </div>
                :
                renderCampaignDetails()
            }
        </PageSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    marketingCampaignDetailsLoading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
    },
    unsupportedCampaignDetails: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
    }
}

export default MarketingCampaignDetails;
